import { useState, useContext } from 'react';
import Context from '@/utils/context';
import { history } from 'umi';
import { useSelector } from 'dva';
import { useIntl } from 'react-intl';
import { Row, Col } from 'antd';
import { Form, message } from 'iglooform';
import { BackButton } from '@/components/back-button/back-button';
import { SuccessFilled } from 'iglooicon';
import throttle from 'lodash/throttle';
import './index.less';
import { changePassword } from '../service';
import { desEncryptLoginPassword } from '@/utils/common';
import { reg } from '@/utils/regexp';

const ChangePasswordPage = () => {
  const { formatMessage } = useIntl();
  const { isPc } = useContext(Context);
  const form = Form.useForm();
  const { userInfo } = useSelector<ReduxState, GlobalState>(
    (state) => state.global,
  );
  const [passwordSuccess, setPasswordSuccess] = useState<boolean>(false);

  const handleChangePassword = throttle(
    async () => {
      try {
        const values = await form.validateFields();
        const { old_password, new_password } = values;
        changePassword({
          old_password: desEncryptLoginPassword(old_password),
          new_password: desEncryptLoginPassword(new_password),
          email: userInfo ? userInfo.email : '',
        }).then((res) => {
          if (res && res.status === 'ok') {
            message.success(
              formatMessage({ id: 'Change password successfully' }),
            );
            history.replace('/home');
          }
        });
      } catch (error) {
        console.log(error);
      }
    },
    1000,
    { leading: true, trailing: false },
  );

  return (
    <>
      <BackButton handleClick={() => history.goBack()} />
      <Row style={isPc ? { marginTop: '40px' } : { marginTop: '4px' }}>
        <Col
          span={isPc ? 12 : 24}
          style={{ maxWidth: '538px', margin: '0 auto' }}
        >
          <div className="form-container">
            <Form
              form={form}
              config={{
                type: 'Login',
                elements: [
                  {
                    type: 'Password',
                    label: formatMessage({
                      id: 'Current Password',
                      defaultMessage: 'Current Password',
                    }),
                    name: 'old_password',
                    fullRow: true,
                    required: true,
                    initialValue: '',
                    rules: [
                      {
                        validator: (rule: any, value: string) => {
                          if ((!!value && !reg.test(value)) || !value) {
                            return Promise.reject(
                              new Error(
                                formatMessage({
                                  id: 'Invalid Password',
                                }),
                              ),
                            );
                          } else {
                            return Promise.resolve();
                          }
                        },
                      },
                    ],
                  },
                  {
                    type: 'Password',
                    label: formatMessage({
                      id: 'New Password',
                      defaultMessage: 'New Password',
                    }),
                    name: 'new_password',
                    fullRow: true,
                    required: true,
                    initialValue: '',
                    extra: [
                      <div key="0">
                        <SuccessFilled
                          className={
                            passwordSuccess
                              ? 'passwordSuccessIcon'
                              : 'passwordUnsuccessIcon'
                          }
                        />
                        <span>
                          {formatMessage({
                            id: 'At least 8 characters long.',
                            defaultMessage: 'At least 8 characters long.',
                          })}
                        </span>
                      </div>,
                      <div key="1">
                        <SuccessFilled
                          className={
                            passwordSuccess
                              ? 'passwordSuccessIcon'
                              : 'passwordUnsuccessIcon'
                          }
                        />
                        <span>
                          {formatMessage({
                            id: 'Contains uppercase & lowercase letter.',
                            defaultMessage:
                              'Contains uppercase & lowercase letter.',
                          })}
                        </span>
                      </div>,
                      <div key="2">
                        <SuccessFilled
                          className={
                            passwordSuccess
                              ? 'passwordSuccessIcon'
                              : 'passwordUnsuccessIcon'
                          }
                        />
                        <span>
                          {formatMessage({
                            id: 'Contains at least one special character.',
                            defaultMessage:
                              'Contains at least one special character.',
                          })}
                        </span>
                      </div>,
                      <div key="3">
                        <SuccessFilled
                          className={
                            passwordSuccess
                              ? 'passwordSuccessIcon'
                              : 'passwordUnsuccessIcon'
                          }
                        />
                        <span>
                          {formatMessage({
                            id: 'Contains numbers.',
                            defaultMessage: 'Contains numbers.',
                          })}
                        </span>
                      </div>,
                    ],
                    rules: [
                      {
                        validator: (rule: any, value: string) => {
                          const p = form.getFieldValue('confirmPassword');

                          if ((!!value && !reg.test(value)) || !value) {
                            if (passwordSuccess) {
                              setPasswordSuccess(false);
                            }
                            if (!value) {
                              return Promise.reject();
                            }
                            return Promise.reject(
                              new Error(
                                formatMessage({
                                  id: 'Invalid Password',
                                }),
                              ),
                            );
                          } else if (!!p && p !== value) {
                            if (!passwordSuccess) {
                              setPasswordSuccess(true);
                            }
                            return Promise.reject(
                              new Error(
                                formatMessage({
                                  id: 'The two passwords are inconsistent',
                                }),
                              ),
                            );
                          } else {
                            value
                              ? setPasswordSuccess(true)
                              : setPasswordSuccess(false);
                            return Promise.resolve();
                          }
                        },
                      },
                    ],
                    antdDependencies: ['confirmPassword'],
                  },
                  {
                    type: 'Password',
                    label: formatMessage({
                      id: 'Confirm New Password',
                      defaultMessage: 'Confirm New Password',
                    }),
                    name: 'confirmPassword',
                    fullRow: true,
                    required: true,
                    initialValue: '',
                    rules: [
                      {
                        validator: (rule: any, value: string) => {
                          const p = form.getFieldValue('new_password');
                          if (!!value && p !== value) {
                            return Promise.reject(
                              new Error(
                                formatMessage({
                                  id: 'The two passwords are inconsistent',
                                }),
                              ),
                            );
                          } else {
                            return Promise.resolve();
                          }
                        },
                      },
                    ],
                    antdDependencies: ['new_password'],
                  },
                ],
              }}
              onSubmit={handleChangePassword}
              locales={{
                submitText: formatMessage({
                  id: 'Done',
                  defaultMessage: 'Done',
                }),
                validateMessages: {
                  required:
                    '${label} ' +
                    formatMessage({
                      id: 'is required',
                    }),
                },
              }}
            />
          </div>
        </Col>
      </Row>
    </>
  );
};

export default ChangePasswordPage;
